import { dateTypeMap } from '@op/constants';
import { ParamsTimeType } from '@op/types/common';
import {
  arrayToSimpleObj,
  getRationPercentValue,
  getTimeSpaceArray,
  uniqueArray,
} from '@op/utils';
import {
  brandHealthFormat,
  dealwithBrandHealth,
  getDefaultValue,
} from '@op/utils/value';
import moment from 'moment';
import { METRICS_KEY_WITH_ZERO, METRICS_KEY_ZERO_NO_RATIO } from '../constant';

interface ITable {
  time: string;
  name: string;
  value: number;
  ratio: number;
  game_id: string;
}

interface ITimeline {
  [metrics: string]: {
    xAxisData: {
      id: string;
      data: any;
      color: string;
    }[];
    yAxisData: {
      name: string;
      id: string;
      data: any;
      color: string;
    }[];
    tableData: ITable[];
    isEmpty: boolean;
    length: number;
  };
}

export const formatCompareCompetitorsTimeline = (data, _filter, extraData) => {
  const { metrics, compare_games } = _filter || {};
  const { gameData, dateType: date_type, color } = extraData || {};
  const { timeline_list } = data;
  const metricsListTimeline: ITimeline = {};
  const sortedTimeline = timeline_list.sort((a, b) => a.index - b.index);
  const gameIndex: { [key: string]: number } = {};
  let indexNumber = 0;
  compare_games?.forEach((val) => {
    const { edition_unified_id } = val;
    if (gameIndex[edition_unified_id]) {
      return;
    }
    gameIndex[edition_unified_id] = indexNumber;
    indexNumber += 1;
  });

  metrics.forEach((metricsKey: string) => {
    let dataCount = 0;
    let timeList: Array<string> = [];
    let length = 0;
    const dataList: { [key: string]: ITable } = {};
    const timelineList: {
      id: string;
      data: any;
      color: string;
      game_id: string;
    }[] = [];
    const records =
      sortedTimeline?.map((val) => {
        const {
          game_id,
          list,
          timeline_of_start_time,
          timeline_of_end_time,
          start_time,
          end_time,
          index: gameIndex,
        } = val;
        const id = `${game_id}_${start_time}_${end_time}_${gameIndex}`;
        const name = gameData?.[game_id]?.entity_name || game_id;
        const release = gameData?.[game_id]?.release_time;
        const dateType = dateTypeMap[date_type as ParamsTimeType];

        let format = 'YYYY-MM-DD';
        if (dateType === 'hour') {
          format = 'YYYY-MM-DD HH:mm:ss';
        } else if (dateType === 'month') {
          format = 'YYYY-MM';
        }

        timeList = getTimeSpaceArray(
          timeline_of_start_time,
          timeline_of_end_time,
          dateType,
          format,
        );

        length = length > timeList.length ? length : timeList.length;
        const valueMap = arrayToSimpleObj(list, metricsKey, (item) =>
          moment(item.time).format(format),
        );
        const data = timeList.map((time, index) => {
          let value = valueMap[time] || null;
          if (value) {
            dataCount += 1;
          }

          if (value === null && METRICS_KEY_WITH_ZERO.includes(metricsKey)) {
            value = 0;
          } else if (metricsKey === 'brand_health') {
            value = dealwithBrandHealth(getDefaultValue(value, 'brand_health'));
          }

          // 这个key有问题，因为index为0的时候，前面是没有值的
          let oldKey = timeList?.[index - 1];
          if (index === 0) {
            oldKey = moment(time).subtract(1, dateType).format(format);
          }
          const oldValue = valueMap[oldKey] || 0;
          const ratio = getRationPercentValue(value, oldValue, 6);

          dataList[time + name + value] = {
            time,
            name,
            value,
            ratio,
            game_id,
          };
          return {
            value,
            time,
          };
        });

        timelineList.push({
          id,
          data: timeList,
          game_id,
          color: color[id],
        });

        return {
          name,
          game_id,
          id,
          data,
          color: color[id],
          start_time,
          end_time,
          release,
        };
      }) || [];

    metricsListTimeline[metricsKey] = {
      xAxisData: timelineList,
      yAxisData: records,
      length,
      tableData: Object.values(dataList).sort((a, b) => {
        const gameComparison = gameIndex[a.game_id] - gameIndex[b.game_id];
        if (gameComparison !== 0) {
          return gameComparison;
        }
        return new Date(b.time).getTime() - new Date(a.time).getTime();
      }),
      isEmpty: dataCount === 0,
    };
  });

  return metricsListTimeline;
};

export const formatCompareCompetitorsKeyMetrics = (data: any, _filter: any) => {
  const { metrics: keys } = _filter;
  const { list } = data;
  const formattedData: Array<any> = [];
  let isEmpty = 0;

  // const totalData: { [key: string]: number } = {};
  const maxValues: { [key: string]: number } = {};
  list.forEach((val: any) => {
    const { list } = val || {};
    keys.forEach((key: string) => {
      const value = list?.[key] || 0;

      // 累加totalData
      // const newTotalKey = `${key}_total`;
      // if (!totalData?.[newTotalKey]) {
      //   totalData[newTotalKey] = 0;
      // }
      // totalData[newTotalKey] += value || 0;

      // 计算maxValues
      const newMaxKey = `${key}_max`;
      const currentValue = typeof value === 'number' ? value : 0;
      if (currentValue > (maxValues[newMaxKey] || 0)) {
        maxValues[newMaxKey] = currentValue;
      }
    });
  });

  list.forEach((val: any) => {
    const {
      game_id,
      link_of_end_time,
      link_of_start_time,
      list,
      old_list,
      start_time,
      end_time,
      index,
    } = val || {};
    const id = `${game_id}_${start_time}_${end_time}_${index}`;
    const valueMap = keys.reduce((pre: any, key: string) => {
      const isBrandHealth = key === 'brand_health';
      const oldKey = `${key}_old`;
      const ratioKey = `${key}_ratio`;
      let value = list?.[key] || 0;
      let oldValue = old_list?.[key] || 0;
      let ratio;

      if (isBrandHealth) {
        value = list?.[key] === null ? null : brandHealthFormat(value);
        oldValue =
          old_list?.[key] === null ? null : brandHealthFormat(oldValue);
        ratio = getRationPercentValue(value, oldValue, 6);
      } else if (
        (value === 0 || oldValue === 0) &&
        METRICS_KEY_ZERO_NO_RATIO.includes(key)
      ) {
        ratio = 0;
      } else {
        ratio = getRationPercentValue(value, oldValue, 6);
      }

      if (!isNaN(Number(value)) && Number(value) !== 0) {
        isEmpty += 1;
      }
      return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio };
    }, {});

    formattedData.push({
      game_id,
      edition_unified_id: game_id,
      link_of_end_time,
      link_of_start_time,
      id,
      start_time,
      end_time,
      ...valueMap,
      // ...totalData,
      ...maxValues,
    });
  });

  // console.log(formattedData);

  return formattedData;
};
